import { useState } from "react";
import { useForm } from "react-hook-form";

import AccountImageUpload from "@/components/account-image-upload";
import Button from "@/components/common/input/button";
import Input from "@/components/common/input/input";
import Modal from "@/components/common/modal";
import ErrorMessage from "@/components/error-message";
import UserBadge from "@/components/user-badge";
import { handled, logRejection } from "@/helpers";
import { useAuth } from "@/hooks/auth";
import useSnackbar from "@/hooks/snackbar";

const AccountSettings = () => {
  const { user, is_loading } = useAuth();

  if (is_loading) {
    return null;
  }

  if (!user) {
    return <div>Not logged in</div>;
  }

  return (
    <div className="mx-auto mt-12 max-w-[40rem]">
      <div className="flex items-center justify-between">
        <UserBadge user={user} include_email />
        <div className="flex flex-col gap-4">
          <Modal>
            <Button>Edit</Button>
            <EditUserForm />
          </Modal>
          {!user.external_auth && (
            <Modal>
              <Button>Change password</Button>
              <UpdatePasswordForm />
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
};

interface UpdatePasswordFormValues {
  new_password: string;
  new_password_again: string;
  current_password: string;
}

interface UpdatePasswordFormProps {
  toggleFn?: () => void;
}

const UpdatePasswordForm = ({ toggleFn }: UpdatePasswordFormProps) => {
  const { error, updateUserPassword } = useAuth();
  const [show_password, setShowPassword] = useState(false);
  const { showSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UpdatePasswordFormValues>();

  const _handleSubmit = (form_data: UpdatePasswordFormValues) => {
    clearErrors();
    if (form_data.new_password !== form_data.new_password_again) {
      setError("new_password_again", { message: "Passwords do not match" });
      return;
    }
    updateUserPassword(form_data.new_password, form_data.current_password)
      .then((response) => {
        if (response.is_ok) {
          reset();
          showSnackbar("Password changed");
          if (toggleFn) toggleFn();
        } else {
          // `error` is the auth context's own state and can be `true`, which
          // react-hook-form cannot render as a message.
          setError("current_password", {
            message:
              response.json.detail === "error/wrong-password"
                ? "Wrong password"
                : (response.json.detail ??
                  (typeof error === "string" ? error : "Could not change the password")),
          });
        }
      })
      .catch(logRejection);
  };

  const _handleCancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    if (toggleFn) toggleFn();
  };

  return (
    <div className="mt-4 w-[30rem] rounded-lg bg-slate-800 p-8">
      <div className="mb-2 flex justify-between">
        <div className="text-xl text-slate-400">Change password</div>
        <button
          type="button"
          onClick={() => setShowPassword(!show_password)}
          title={show_password ? "Hide password" : "Show password"}
          aria-label={show_password ? "Hide password" : "Show password"}
          aria-pressed={show_password}
          className="material-symbols-rounded notranslate cursor-pointer select-none"
        >
          {show_password ? "visibility" : "visibility_off"}
        </button>
      </div>
      <form onSubmit={handled(handleSubmit(_handleSubmit))}>
        <Input
          type={show_password ? "text" : "password"}
          label="New password"
          id="new_password"
          error={errors.new_password?.message}
          register={register}
          required
        />
        <Input
          type={show_password ? "text" : "password"}
          label="New password again"
          id="new_password_again"
          error={errors.new_password_again?.message}
          register={register}
          required
        />
        <Input
          type={show_password ? "text" : "password"}
          label="Current password"
          id="current_password"
          error={errors.current_password?.message}
          register={register}
          required
        />
        <div className="flex items-center gap-4">
          <Button type="submit">Update password</Button>
          <Button onClick={_handleCancel}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};

interface EditUserFormValues {
  name: string;
  title: string;
}

interface EditUserFormProps {
  toggleFn?: () => void;
  callbackFn?: () => void;
}

const EditUserForm = ({ toggleFn, callbackFn }: EditUserFormProps) => {
  const [error, setError] = useState<string | null>(null);
  const { user, updateUser } = useAuth();
  // The form edits these two fields, so it seeds them rather than spreading the
  // whole account into the form state.
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditUserFormValues>({
    defaultValues: { name: user?.name ?? "", title: user?.title ?? "" },
  });
  const [photo, setPhoto] = useState<File | string | null>(user?.photo_url ?? null);

  const _onSubmit = (form_data: EditUserFormValues) => {
    updateUser(form_data.name, form_data.title || "", photo, Boolean(user?.photo_url && !photo))
      .then((success) => {
        if (success) {
          if (callbackFn) callbackFn();
          if (toggleFn) toggleFn();
        } else {
          setError("Server error. Please try again.");
        }
      })
      .catch((_error) => {
        setError(_error);
      });
  };

  const _handleCancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    if (toggleFn) toggleFn();
  };

  return (
    <div className="mt-4 w-[30rem] rounded-lg bg-slate-800 p-8">
      <form onSubmit={handled(handleSubmit(_onSubmit))}>
        <Input
          type="text"
          label="Name"
          id="name"
          register={register}
          register_options={{ required: "Name is required" }}
          error={errors.name?.message}
          required
        />
        <Input type="text" label="Title" register={register} id="title" />
        <AccountImageUpload value={photo} onChange={setPhoto} />
        <div className="flex items-center gap-4">
          <Button type="submit">Submit</Button>
          <Button onClick={_handleCancel}>Cancel</Button>
        </div>
      </form>
      <ErrorMessage message={error} />
    </div>
  );
};

export default AccountSettings;
